import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "src/shared/store";
import { StartStatuses } from "./model/start-statuses";
import { VideoModes } from "./model/video-modes";

export interface VideoState {
  startStatus: StartStatuses;
  isPlaying: boolean;
  mode: VideoModes;
  READONLY_currentTime: number;
  WRITEONLY_currentTime: number;
  duration: number;
}

const initialState: VideoState = {
  startStatus: StartStatuses.NOT_SET,
  isPlaying: false,
  mode: VideoModes.NORMAL,
  READONLY_currentTime: 0,
  WRITEONLY_currentTime: 0,
  duration: 0,
};

export const videoSlice = createSlice({
  name: "video",
  initialState,
  reducers: {
    setStartStatus: (
      state: VideoState,
      action: PayloadAction<StartStatuses>
    ) => {
      if (state.startStatus === action.payload) return;
      state.startStatus = action.payload;
    },
    setIsPlaying: (state: VideoState, action: PayloadAction<boolean>) => {
      if (state.isPlaying === action.payload) return;
      state.isPlaying = action.payload;
    },
    setVideoMode: (state: VideoState, action: PayloadAction<VideoModes>) => {
      if (state.mode === action.payload) return;
      state.mode = action.payload;
    },
    toggleVideoMode: (state: VideoState, action: PayloadAction<void>) => {
      state.mode =
        state.mode === VideoModes.NORMAL
          ? VideoModes.PICTURE_IN_PICTURE
          : VideoModes.NORMAL;
    },
    // does not update video element
    setCurrentTime: (state: VideoState, action: PayloadAction<number>) => {
      state.READONLY_currentTime = action.payload;
    },

    // does update video element
    updateCurrentTimeWithPercentage: (
      state: VideoState,
      action: PayloadAction<number>
    ) => {
      const onePercent = state.duration / 100;
      state.WRITEONLY_currentTime = onePercent * action.payload;
    },
    forwardCurrentTime: (state: VideoState, action: PayloadAction<number>) => {
      state.WRITEONLY_currentTime += action.payload;
    },
    rewindCurrentTime: (state: VideoState, action: PayloadAction<number>) => {
      state.WRITEONLY_currentTime -= action.payload;
    },
    setDuration: (state: VideoState, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
  },
});

export const {
  setStartStatus,
  setIsPlaying,
  setVideoMode,
  toggleVideoMode,
  setCurrentTime,
  updateCurrentTimeWithPercentage,
  forwardCurrentTime,
  rewindCurrentTime,
  setDuration,
} = videoSlice.actions;

export const videoStatusSelector = (state: RootState) =>
  state.video.startStatus;
export const isPlayingSelector = (state: RootState) => state.video.isPlaying;
export const durationSelector = (state: RootState) => state.video.duration;
export const currentTimeSelector = (state: RootState) =>
  state.video.READONLY_currentTime;
export const WRITEONLY_currentTimeSelector = (state: RootState) =>
  state.video.WRITEONLY_currentTime;
export const videoModeSelector = (state: RootState) => state.video.mode;

export default videoSlice.reducer;
